import * as _ from 'lodash';
import { Component } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { plural } from '../../../../shared/helpers';
import Icon, { icons, IconText } from '../../generic/Icon';
import type { Match, RevisionLicense } from '../../../types';

// A LicenseGroupId is the ID of a LicenseGroup for actual license groups (i.e., for RevisionLicenses that have a
// licenseGroupId set) or an object with the ID of the RevisionLicense itself (i.e., for RevisionLicenses that have a
// null licenseGroupId).
export type LicenseGroupId = number | { revisionLicenseId: number };

// A NormalizedMatch combines all the matches with a particular path.
export type NormalizedMatch = {
  path: string;
  // originalMatches is all of the combined original matches.
  originalMatches: Match[];
  // sources is all the sources arrays from the combined matches.
  sources: string[];
};

export type NormalizedLicenseGroup = {
  groupId: LicenseGroupId;
  revisionLicenses: RevisionLicense[];
  // licenseName is the name of the license group as output by "getMultiLicenseTitle".
  licenseName: string;
  isDeclared: boolean;
  isConcluded: boolean;
  matches: NormalizedMatch[];
};

interface LicenseMatchListProps {
  licenseGroups: NormalizedLicenseGroup[];
  displayMode: 'single' | 'multiple';
  matchLimit?: number;
  onShowLicenseMatch(group: NormalizedLicenseGroup, filePath: string): void;
}

class LicenseMatchList extends Component<LicenseMatchListProps> {
  declaredLicenseOverlay = (<Tooltip>Declared by developers in package metadata</Tooltip>)

  concludedLicenseOverlay = (<Tooltip>This license was determined by FOSSA</Tooltip>)

  renderMatchTitle = (group: NormalizedLicenseGroup) => {
    let tooltip: string | null = null;
    let titleClass;
    let matchIcon;

    if (group.isDeclared) {
      tooltip = 'Primary License';
      titleClass = 'match-primary';
      matchIcon = (
        <span className="clickable margin-right-xs">
          <Icon icon={icons.checkCircle} />
        </span>
      );
    } else if (group.revisionLicenses.length > 1) {
      tooltip = 'Multi-Licensed; you may choose any of these to use the code under';
      titleClass = 'match-secondary';
      matchIcon = (
        <span className="clickable margin-right-xs">
          <Icon icon={icons.questionCircle} />
        </span>
      );
    }

    return (
      <li className="header">
        <span className="match-title">
          <OverlayTrigger
            placement="left"
            overlay={tooltip ? <Tooltip>{tooltip}</Tooltip> : <span />}
          >
            <span className={titleClass}>
              {matchIcon}
              <b className="inline-header">
                {group.licenseName}
              </b>
            </span>
          </OverlayTrigger>{' '}
          <code>
            {group.matches.length} {plural(group.matches.length, 'File')}
          </code>
        </span>
      </li>
    );
  };

  renderLicenseGroup = (group: NormalizedLicenseGroup, i: number) => {
    const matchLimit = this.props.matchLimit || 5;
    return (
      <ul key={`match_${i}`} className="matches-list list-min">
        {this.renderMatchTitle(group)}
        {group.isDeclared && (
          <li>
            <OverlayTrigger
              placement="left"
              overlay={this.declaredLicenseOverlay}
            >
              <b>
                <IconText
                  text="DECLARED LICENSE"
                  icon={icons.star}
                  alignIcon="right"
                />
              </b>
            </OverlayTrigger>
          </li>
        )}
        {group.isConcluded && (
          <li>
            <OverlayTrigger
              placement="left"
              overlay={this.concludedLicenseOverlay}
            >
              <b>
                CONCLUDED LICENSE
              </b>
            </OverlayTrigger>
          </li>
        )}
        {group.matches.map(({ path }, j) => {
          if (j >= matchLimit) {
            return false;
          }
          return (
            <li key={path}>
              <a
                onClick={() => {
                  this.props.onShowLicenseMatch(group, path);
                }}
                role="button"
              >
                {path}
              </a>
            </li>
          );
        })}
        {group.matches.length > matchLimit && (
          <li className="text-center" style={{ marginTop: '10px', opacity: 0.6 }}>
            <a
              onClick={() => {
                this.props.onShowLicenseMatch(group, (_.head(group.matches) as NormalizedMatch).path);
              }}>
              <i>{group.matches.length - matchLimit} More...</i>
            </a>
          </li>
        )}
      </ul>
    );
  };

  render() {
    const { licenseGroups } = this.props;

    return (
      <>
        {licenseGroups.length === 0 && (
          <p>
            <IconText
              icon={icons.times}
              text={`No licenses found.${this.props.displayMode === 'single' ? '' : ' Try selecting a dependency from the dropdown.'}`}
            />
          </p>
        )}
        {licenseGroups.map(this.renderLicenseGroup)}
      </>
    );
  }
}

export default LicenseMatchList;
